// @ts-strict-ignore
import * as React from 'react';
import {useEffect} from 'react';
import clsx from 'clsx';

import {useDataGroups} from 'common/contexts/react/data-group-context';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {DEFAULT_DATA_GROUP} from 'common/widgets/data-group-selector';
import {SelectReact} from 'common/components/select';
import {SelectTheme} from 'common/components/select/constants';
import {Directions} from 'common/components/drop-menu/types';
import {ButtonReact} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {useSessionStorageManager} from 'common/contexts/react/storage-manager-context';
import {useTranslation} from 'common/i18n/hooks';

import styles from './index.module.styl';

const DATA_GROUP_SHOW_TOOLTIP_THRESHOLD = 10;
const LAST_SELECTED_DATA_GROUP_KEY = `last-selected-data-group-key`;

const buttonStyleOverride = `
  :host(:focus) .mp-button-container {
    background-color: inherit !important;
  }
`;

const selectStyleOverride = `
  .select-container .select-trigger mp-button {
    --button-content-width: auto;
  }
`;

function DataGroupItem(props: {dataGroups: DataGroup[]; selected: string; onSelect: (dataGroupId: string) => void}) {
  const selected = props.selected === undefined ? DEFAULT_DATA_GROUP.groupID : props.selected;
  const isSelected = props.dataGroups.find((dataGroup) => dataGroup.groupID === selected);
  const sessionStorageManager = useSessionStorageManager();
  const {t} = useTranslation();

  if (props.dataGroups.length === 1) {
    const isDefault = props.dataGroups[0].groupID === DEFAULT_DATA_GROUP.groupID;

    return (
      <ButtonReact
        styleOverride={buttonStyleOverride}
        className={clsx(styles.groupSelector, {[styles.isSelected]: isSelected})}
        icon={isDefault ? `user` : `team`}
        onClick={() => props.onSelect(props.dataGroups[0].groupID)}
        theme={isSelected ? ButtonTheme.Secondary : ButtonTheme.Tertiary}
      >
        {isDefault ? t(`query-builder.dg-user`) : props.dataGroups[0].displayName}
      </ButtonReact>
    );
  }

  const items = props.dataGroups.map((dataGroup) => ({
    isDisabled: !dataGroup.isReady,
    isSelected: props.selected === dataGroup.groupID,
    label: dataGroup.displayName,
    tooltip:
      dataGroup.displayName.length > DATA_GROUP_SHOW_TOOLTIP_THRESHOLD
        ? {
            text: dataGroup.displayName,
          }
        : null,
    value: dataGroup.groupID,
  }));
  const selectedItem = props.dataGroups.find((dataGroup) => dataGroup.groupID === props.selected);
  const lastSelectedDataGroupItem = props.dataGroups.find(
    (dataGroup) => dataGroup.groupID === sessionStorageManager.getItem(LAST_SELECTED_DATA_GROUP_KEY),
  );
  const selectedLabel =
    selectedItem?.displayName || lastSelectedDataGroupItem?.displayName || props.dataGroups[0].displayName;
  return (
    <SelectReact
      className={clsx(styles.groupSelector, {[styles.isSelected]: isSelected})}
      styleOverride={selectStyleOverride}
      theme={isSelected ? SelectTheme.Secondary : SelectTheme.Tertiary}
      icon={{
        leftIcon: `team`,
      }}
      alwaysShowCaret
      disableSearch
      menuAlignment={Directions.DownRight}
      onChange={(ev) => {
        sessionStorageManager.setItem(LAST_SELECTED_DATA_GROUP_KEY, ev.detail.item.value);
        props.onSelect(ev.detail.item.value);
      }}
      tooltip={
        selectedLabel.length > DATA_GROUP_SHOW_TOOLTIP_THRESHOLD
          ? {
              text: selectedLabel,
            }
          : null
      }
      label={selectedLabel}
      sections={[
        {
          items,
        },
      ]}
    />
  );
}

type Props = {
  projectId: number;
  workspaceId: number;
  // string | null to allow callers to be strict, since DEFAULT_DATA_GROUP has a null id.
  selected: string | null;
  onSelect: (dataGroupId: string) => void;
};
export function SidebarDataGroupSelector(props: Props) {
  const dataGroupStore = useDataGroups();
  useEffect(() => {
    dataGroupStore.fetch({
      projectId: props.projectId,
      workspaceId: props.workspaceId,
    });
  }, []);
  // if the dataGroupStore is not populated it's falsy as well
  // no data group then do not show it at all
  // by default we analyze by "user"
  if (!dataGroupStore.value || Object.values(dataGroupStore.value).length === 0) {
    return null;
  }
  // design is to display a "radio button" like layout
  return (
    <div className={styles.container} elref='dataGroupSelector'>
      <DataGroupItem dataGroups={[DEFAULT_DATA_GROUP]} selected={props.selected} onSelect={props.onSelect} />
      <DataGroupItem
        dataGroups={Object.values(dataGroupStore.value)}
        selected={props.selected}
        onSelect={props.onSelect}
      />
    </div>
  );
}
